<template>
  <div>
    <q-dialog v-model="isDialogueVisible" @hide="resetData">
      <q-card class="new-function-card">
        <q-form @submit.prevent="onSubmit" class="q-gutter-md">
          <q-card-section class="title">
            <div v-if="duplicateCurrent">Create user function from {{ userFunction.id }}</div>
            <div v-else>Create new user function</div>
          </q-card-section>

          <q-scroll-area class="scroll-area">
            <q-card-section class="content-wrapper">
              <q-input
                :error="v$.name.$error && !v$.name.$pending"
                :error-message="
                  v$.name.required.$invalid
                    ? v$.name.required.$message
                    : v$.name.minLength.$invalid
                      ? v$.name.minLength.$message
                      : v$.name.maxLength.$invalid
                        ? v$.name.maxLength.$message
                        : v$.name.pep8.$invalid
                          ? v$.name.pep8.$message
                          : v$.name.isUnique.$invalid
                            ? v$.name.isUnique.$message
                            : undefined
                "
                :loading="v$.name.isUnique.$pending"
                autofocus
                bottom-slots
                debounce="500"
                filled
                label="Name"
                placeholder="e.g.: split_date"
                type="text"
                v-model="state.name"
              />

              <q-input
                :error-message="v$.issue.required.$message"
                :error="v$.issue.$error"
                @keyup.enter="prompt = false"
                class="q-mt-md q-mb-md"
                filled
                label="Issue reference"
                placeholder="e.g.: Q2200946 or IRIS-4279"
                type="text"
                v-model="state.issue"
              >
                <template #hint v-if="state.issue && state.issue !== normalizedIssue">
                  Will be normalized to <b>{{ normalizedIssue }}</b>
                </template>
              </q-input>

              <q-input
                :error-message="v$.description.required.$message"
                :error="v$.description.$error"
                dense
                filled
                label="Description"
                placeholder="Some information on what this function does"
                size="sm"
                type="textarea"
                v-model="state.description"
              />

              <div class="q-mt-md q-mr-md">
                Please note that:
                <ul>
                  <li>The chosen <b>name</b> is final and <b>cannot be changed later</b>.</li>
                  <li><b>Trivial functions</b> should be avoided.</li>
                  <li>Functions are meant to be <b>re-used across rules</b>.</li>
                  <li>Functions are <b>not variables</b> and can be called with parameters.</li>
                  <li>
                    If possible, try to write <b>logic that can be re-used</b> to compute <b>multiple variables</b>.
                  </li>
                </ul>
                <br />
                <q-item dense tag="label" v-ripple>
                  <div avatar class="checkbox">
                    <q-checkbox
                      dense
                      size="sm"
                      v-model="state.userAgreement"
                      class="q-pl-sm"
                      label="I understand and want to proceed with creating a new function"
                    >
                    </q-checkbox>
                  </div>
                </q-item>
              </div>
            </q-card-section>
          </q-scroll-area>

          <q-separator class="separator" />
          <q-card-actions align="right" class="text-secondary card-actions">
            <q-btn class="btn-cancel" flat label="Cancel" v-close-popup />

            <q-btn
              v-if="duplicateCurrent"
              :disable="v$.$invalid"
              :loading="isLoading"
              class="btn-add"
              color="secondary"
              label="Duplicate"
              type="submit"
            />

            <q-btn
              v-else
              :disable="v$.$invalid"
              :loading="isLoading"
              class="btn-add"
              color="secondary"
              label="Create"
              type="submit"
            />
          </q-card-actions>
        </q-form>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { GIT_RELEASE_BRANCH } from '@/config'
import { paths } from '@/router'
import type { UserFunction } from '@/types'
import { LATEST_SUPPORTED_USER_FUNCTION_FORMAT_VERSION } from '@/utils/constants'
import { normalizeIssueRef } from '@/utils/issues'
import notifications from '@/utils/notifications'
import { useDirectStore } from '@/utils/utils'
import useVuelidate from '@vuelidate/core'
import { helpers, maxLength, minLength, required, sameAs } from '@vuelidate/validators'
import { isAxiosError } from 'axios'
import { computed, defineComponent, reactive } from 'vue'
import { useRouter } from 'vue-router/composables'

interface Props {
  visible: boolean
  duplicateCurrent: boolean
  userFunction: UserFunction
}

export default defineComponent<Props>({
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    userFunction: {
      type: Object,
      default: undefined,
    },
    duplicateCurrent: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, ctx) {
    const { state, dispatch } = useDirectStore()
    const router = useRouter()
    const isLoading = computed(() => state.userFunction.isLoading)

    const isDialogueVisible = computed({
      get() {
        return props.visible
      },
      set(val) {
        if (!val) {
          ctx.emit('close')
        }
      },
    })

    async function isUserFunctionNameUnique(value: string) {
      if (!value) {
        return true
      }

      if (v$.value.name.$invalid) {
        return true
      }

      try {
        await dispatch.userFunction.GET_USER_FUNCTION({
          userFunctionId: value,
          branch: GIT_RELEASE_BRANCH,
        })
        // `GET /user-functions/{userFunctionId}/branches/release` succeeded (HTTP 200)
        // Which means that the specified user function name is already in used
        return false
      } catch (err) {
        // `GET /user-functions/{userFunctionId}/branches/release` returned a non-200 response
        // If we got a 404, it means the user function name is not taken and the validator should return `true`
        return isAxiosError(err) && err.response?.status === 404
      }
    }

    const data = reactive({
      description: '',
      issue: '',
      labels: [],
      name: '',
      userAgreement: false,
    })

    const rules = computed(() => ({
      description: { required },
      issue: { required },
      name: {
        isUnique: helpers.withMessage(
          'User function with that name already exists',
          helpers.withAsync(isUserFunctionNameUnique)
        ),
        maxLength: maxLength(60),
        minLength: minLength(2),
        pep8: helpers.withMessage(
          'Should be lowercase, with words separated by underscores',
          helpers.regex(/^[a-z0-9_]*$/)
        ),
        required,
      },
      userAgreement: { required, accepted: sameAs(true) },
    }))

    const normalizedIssue = computed(() => normalizeIssueRef(data.issue ?? ''))

    const v$ = useVuelidate(rules, data, { $autoDirty: true, $stopPropagation: true })

    function resetData() {
      data.name = ''
      data.description = ''
      data.labels = []
      data.issue = ''
      data.userAgreement = false
      v$.value.$reset()
    }

    async function onSubmit() {
      if (!v$.value.$invalid) {
        const labels = props.duplicateCurrent ? props.userFunction.labels : []
        const content = props.duplicateCurrent
          ? (props.userFunction.content ?? '').replace(props.userFunction.id, data.name)
          : undefined

        const unittests = props.duplicateCurrent ? props.userFunction.unittests : []
        const keys = props.duplicateCurrent ? props.userFunction.keys : []
        const contentHelpers = props.duplicateCurrent ? props.userFunction.content_helpers : []
        const formatVersion = props.duplicateCurrent
          ? props.userFunction.format_version
          : LATEST_SUPPORTED_USER_FUNCTION_FORMAT_VERSION

        const returnType = props.duplicateCurrent ? props.userFunction.return_type : 'int'
        const sampleValue = props.duplicateCurrent ? props.userFunction.sample_value : 0
        const exampleCode = props.duplicateCurrent
          ? (props.userFunction.example_code || '').replace(props.userFunction.id, data.name)
          : ''
        const standalone = props.duplicateCurrent ? props.userFunction.standalone : true

        const payload = {
          content,
          content_helpers: contentHelpers,
          description: data.description,
          example_code: exampleCode,
          format_version: formatVersion,
          id: data.name,
          jira_issue: normalizedIssue.value,
          keys,
          labels,
          return_type: returnType,
          sample_value: sampleValue,
          standalone,
          unittests,
        }
        await dispatch.userFunction.CREATE_USER_FUNCTION(payload)

        if (props.duplicateCurrent) {
          dispatch.SHOW_NOTIFICATION({
            message: notifications.successfullyDuplicated(props.userFunction.id, data.name),
          })
        }

        router.push(
          paths.userFunctionBuilder
            .replace(':user_function_id', data.name)
            .replace(':branch?', state.auth.user!.username)
        )
      }
    }

    return {
      isDialogueVisible,
      isLoading,
      normalizedIssue,
      onSubmit,
      resetData,
      state: data,
      v$,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/styles/quasar.variables.scss';

.new-function-card {
  flex: 1;
  min-width: 350px;
  position: relative;
  overflow: hidden;
}

.title {
  width: 100%;
  padding: 0.5em 1em;
  font-size: 15px;
  background-color: $info;
  color: white;
  position: sticky;
  top: 0;
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
}

.content-wrapper {
  padding: 0.1em 1em;
  flex: 1;
}

.scroll-area {
  height: 70vh;
}

.card-actions {
  position: sticky;
  bottom: 0;
  padding: 0 1em 1em;
}

.textarea-wrapper {
  margin-bottom: $space-y-md;
}

.separator {
  margin: 1vh 0;
}

.dialogue-head {
  display: flex;
  justify-content: space-between;
}

.function-terms {
  font-size: 12px;
}

.checkbox {
  display: flex;
}
</style>
